'use client';

import { DateTime } from 'luxon';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { CategoryType, CategoryTypeId, CategoryTypeString } from '@diritto/common/content/constants';
import { random, shuffle } from '@diritto/common/utils/array';
import { buildUrl } from '@diritto/common/utils/urls';
import { ExploreContentList } from '@/app/(appHeader)/promotions/2025-pick-and-choo/vote/components/ExploreContentList';
import { useAuthState } from '@/contexts/AuthProvider';
import { useGetContestEntriesQuery } from '@/queries/contests/useGetContestEntriesQuery';
import { configure } from 'utils/configure';

const CategoryNames = [
  CategoryTypeString[CategoryType.Fantasy],
  CategoryTypeString[CategoryType.BL],
  CategoryTypeString[CategoryType.RomanceFantasy],
  CategoryTypeString[CategoryType.Romance],
];
const CategoryIds = [
  CategoryTypeId[CategoryType.Fantasy],
  CategoryTypeId[CategoryType.BL],
  CategoryTypeId[CategoryType.RomanceFantasy],
  CategoryTypeId[CategoryType.Romance],
];

const PageLength = 10;
export default function Explore({ contestId }: { contestId: number }) {
  const { profile } = useAuthState();
  const randomSeed = useMemo(() => {
    const now = DateTime.now().toFormat('MMddHHmm');
    const sessionSeed = random(profile?.id ?? 0, 4);
    const seed = Number(now + sessionSeed);
    return seed;
  }, [profile?.id]);

  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Get a new searchParams string by merging the current
  // searchParams with a provided key/value pair
  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams?.toString());
      params.set(name, value);

      return params.toString();
    },
    [searchParams],
  );

  const categoryIndex = +(searchParams?.get('categoryIndex') ?? -1);
  const displayCount = +(searchParams?.get('display') ?? PageLength);

  const selectedCategoryName = categoryIndex === -1 ? '전체' : CategoryNames[categoryIndex];
  const { data } = useGetContestEntriesQuery(
    contestId,
    selectedCategoryName === '전체' ? undefined : CategoryIds[categoryIndex],
  );
  const contents = useMemo(() => shuffle(data?.contents ?? [], randomSeed), [data?.contents, randomSeed]);
  const changePagination = (params: { categoryIndex?: string; display?: string }) =>
    buildUrl(`${pathname}`, { tab: 'content', ...params });

  const replacePage = useCallback((url: string) => {
    window.history.replaceState(null, '', url);
  }, []);

  return (
    <div className="flex flex-col items-center gap-3 min-h-[800px]">
      <div className="flex flex-col items-center gap-3 w-[100vw] pc:w-[725px] border-0 border-solid border-b border-[#eaeef2] overflow-x-auto">
        <ul className="flex max-w-[400px] pc:max-w-full gap-5 px-4 pc:justify-center">
          <StyledSubcategoryKeywordListItem
            onClick={() => {
              replacePage(changePagination({ categoryIndex: '-1' }));
            }}
            active={selectedCategoryName === '전체'}>
            전체
          </StyledSubcategoryKeywordListItem>
          {CategoryNames.map((categoryName, idx) => (
            <StyledSubcategoryKeywordListItem
              key={categoryName}
              onClick={() => {
                replacePage(changePagination({ categoryIndex: `${idx}` }));
              }}
              active={selectedCategoryName === categoryName}>
              {categoryName}
            </StyledSubcategoryKeywordListItem>
          ))}
        </ul>
      </div>
      <ExploreContentList displayCount={displayCount} contents={contents} />
      {displayCount < contents.length && (
        <button
          className="mt-6 w-[135px] h-[51px]"
          style={{
            backgroundImage: `url(${configure.staticPrefix}/images/contests/3/vote/icons/loadbutton.svg)`,
          }}
          onClick={() => {
            replacePage(`${pathname}?${createQueryString('display', (displayCount + PageLength).toString())}`);
          }}
        />
      )}
      {PageLength < contents.length && contents.length <= displayCount && (
        <button
          className="mt-6 w-[164px] h-[51px]"
          style={{
            backgroundImage: `url(${configure.staticPrefix}/images/contests/3/vote/icons/foldbutton.svg)`,
          }}
          onClick={() => {
            replacePage(`${pathname}?${createQueryString('display', PageLength.toString())}`);
          }}
        />
      )}
    </div>
  );
}
const StyledSubcategoryKeywordListItem = ({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) => (
  <li
    className={`pb-2.5 cursor-pointer text-xs leading-6 ${
      active ? 'text-black font-bold border-0 border-b-2 border-solid border-black' : 'text-gray-400 font-medium'
    }`}
    onClick={onClick}>
    {children}
  </li>
);
